/**
 * src/services/spatial/vectorImport.ts
 *
 * Multi-format vector import for GeoTrust spatial editors. Converts a
 * user-selected file into a normalised GeoJSON `FeatureCollection` so
 * the same downstream code (border editor, spatial-object geometry
 * field, geofence proposal) can ingest Shapefiles, KML, GPX and plain
 * GeoJSON without bespoke parsers per surface.
 *
 * Supported (WASM-free, always bundled):
 *   - .geojson / .json   → native JSON.parse + normalise
 *   - .kml               → @tmcw/togeojson (DOMParser)
 *   - .gpx               → @tmcw/togeojson (DOMParser)
 *   - .zip               → shpjs (zipped Shapefile bundle: .shp/.dbf/.prj)
 *   - .shp               → shpjs (geometry only; attributes need the .dbf)
 *
 * Supported (WASM-backed, lazy-loaded from `./wasmVectorImport` so the
 * engines/CDN binaries are only fetched when the user actually opens one):
 *   - .gpkg              → @ngageoint/geopackage (sql.js / rtree-sql.js WASM)
 *   - .parquet/.geoparquet → parquet-wasm + apache-arrow (GeoParquet WKB)
 *
 * Keep this free of React/Mapbox imports.
 */

import shp from 'shpjs';
import { kml as kmlToGeoJson, gpx as gpxToGeoJson } from '@tmcw/togeojson';
import type { Feature, FeatureCollection, Geometry } from 'geojson';

export type VectorFormat =
  | 'geojson'
  | 'kml'
  | 'gpx'
  | 'shapefile-zip'
  | 'shapefile'
  | 'geopackage'
  | 'geoparquet';

export interface ParseVectorResult {
  featureCollection: FeatureCollection;
  format: VectorFormat;
  /** Number of features parsed. */
  count: number;
}

/** File extensions we know how to read, for input `accept` lists. */
export const VECTOR_IMPORT_ACCEPT =
  '.geojson,.json,.kml,.gpx,.zip,.shp,.gpkg,.parquet,.geoparquet';

function detectFormat(fileName: string): VectorFormat | null {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.geojson') || lower.endsWith('.json')) return 'geojson';
  if (lower.endsWith('.kml')) return 'kml';
  if (lower.endsWith('.gpx')) return 'gpx';
  if (lower.endsWith('.zip')) return 'shapefile-zip';
  if (lower.endsWith('.shp')) return 'shapefile';
  if (lower.endsWith('.gpkg')) return 'geopackage';
  if (lower.endsWith('.parquet') || lower.endsWith('.geoparquet')) return 'geoparquet';
  return null;
}

function readAsText(file: File): Promise<string> {
  return file.text();
}

function readAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return file.arrayBuffer();
}

/** Coerce any GeoJSON object into a FeatureCollection of Features. */
function toFeatureCollection(input: unknown): FeatureCollection {
  if (!input || typeof input !== 'object') {
    throw new Error('Not a recognised GeoJSON object.');
  }
  const obj = input as { type?: string };

  if (obj.type === 'FeatureCollection') {
    const fc = input as FeatureCollection;
    if (!Array.isArray(fc.features)) {
      return { type: 'FeatureCollection', features: [] };
    }
    return fc;
  }
  if (obj.type === 'Feature') {
    return { type: 'FeatureCollection', features: [input as Feature] };
  }
  // Bare geometry (Polygon, MultiPolygon, Point, …)
  if (
    obj.type === 'Polygon' || obj.type === 'MultiPolygon' ||
    obj.type === 'Point' || obj.type === 'MultiPoint' ||
    obj.type === 'LineString' || obj.type === 'MultiLineString' ||
    obj.type === 'GeometryCollection'
  ) {
    return {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties: {}, geometry: input as Geometry }],
    };
  }
  throw new Error(`Unsupported GeoJSON type: ${String(obj.type)}`);
}

function parseXmlDocument(text: string): Document {
  if (typeof DOMParser === 'undefined') {
    throw new Error('XML parsing is only available in a browser environment.');
  }
  const doc = new DOMParser().parseFromString(text, 'application/xml');
  const parserError = doc.getElementsByTagName('parsererror')[0];
  if (parserError) {
    throw new Error('The file is not well-formed XML.');
  }
  return doc;
}

/**
 * Parse a user-selected vector file into a normalised FeatureCollection.
 * Throws a human-readable Error on unsupported or malformed input.
 */
export async function parseVectorFile(file: File): Promise<ParseVectorResult> {
  const format = detectFormat(file.name);
  if (!format) {
    throw new Error(
      `Unsupported file type "${file.name}". Use GeoJSON, KML, GPX, a zipped ` +
        'Shapefile, GeoPackage (.gpkg), or GeoParquet (.parquet).',
    );
  }

  let featureCollection: FeatureCollection;

  switch (format) {
    case 'geojson': {
      const text = await readAsText(file);
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error('Invalid JSON — the file could not be parsed.');
      }
      featureCollection = toFeatureCollection(parsed);
      break;
    }
    case 'kml': {
      const doc = parseXmlDocument(await readAsText(file));
      featureCollection = kmlToGeoJson(doc) as FeatureCollection;
      break;
    }
    case 'gpx': {
      const doc = parseXmlDocument(await readAsText(file));
      featureCollection = gpxToGeoJson(doc) as FeatureCollection;
      break;
    }
    case 'shapefile-zip':
    case 'shapefile': {
      const buffer = await readAsArrayBuffer(file);
      // shpjs returns a FeatureCollection (or an array of them for a zip
      // containing multiple layers). Flatten the multi-layer case.
      const result = await shp(buffer);
      if (Array.isArray(result)) {
        featureCollection = {
          type: 'FeatureCollection',
          features: result.flatMap((fc) => fc.features ?? []),
        };
      } else {
        featureCollection = result as FeatureCollection;
      }
      break;
    }
    case 'geopackage': {
      // Lazy-load the SQLite/WASM engine only when a .gpkg is opened.
      const { parseGeoPackage } = await import('./wasmVectorImport');
      featureCollection = await parseGeoPackage(await readAsArrayBuffer(file));
      break;
    }
    case 'geoparquet': {
      // Lazy-load parquet-wasm + apache-arrow only when a .parquet is opened.
      const { parseGeoParquet } = await import('./wasmVectorImport');
      featureCollection = await parseGeoParquet(await readAsArrayBuffer(file));
      break;
    }
    default:
      throw new Error('Unsupported format.');
  }

  const count = featureCollection.features?.length ?? 0;
  if (count === 0) {
    throw new Error('No features found in the file.');
  }

  return { featureCollection, format, count };
}

/**
 * Pick the first Polygon/MultiPolygon geometry from a FeatureCollection.
 * Border/geofence editors only accept area geometry, so this is the
 * convenience extractor used after `parseVectorFile`.
 */
export function firstPolygonGeometry(
  fc: FeatureCollection,
): Geometry | null {
  for (const feature of fc.features ?? []) {
    const g = feature.geometry;
    if (g && (g.type === 'Polygon' || g.type === 'MultiPolygon')) {
      return g;
    }
  }
  return null;
}
